import React, { useMemo } from 'react';
import {
  CallSharedIcon,
  EPChat,
  EPTranscript,
  MagnifyingGlassIcon,
  MapLayerIcon,
  MarkerIcon,
} from '@rsos/assets/icons';
import {
  Dropdown,
  EmptyState,
  ResultContent,
  ResultIcon,
  ResultItem,
  ResultLabel,
  ResultSublabel,
} from '../GlobalMapSearch.styles';
import { buildHighlightRegex, HighlightedText } from '../highlightMatch';
import type { PreparedItem } from '../useGroupedResults';
import styles from './CommandPalette.module.css';
import type { ArgSuggestion, CommandDef } from './types';

/**
 * Resolve a leading icon for a command. Maps known Tier 1 commands to
 * the same glyphs the live results dropdown uses for the equivalent
 * record (a /call row matches a Call result row), and falls back to a
 * generic search-glyph for anything unrecognized — including future
 * commands added before the palette gets a per-command icon mapping.
 */
function iconForCommand(id: string): React.ReactNode {
  switch (id) {
    case 'call':
      return <CallSharedIcon width={16} height={16} />;
    case 'transcript':
      return <EPTranscript width={16} height={16} />;
    case 'chat':
      return <EPChat width={16} height={16} />;
    case 'parcel':
      return <MapLayerIcon width={16} height={16} />;
    case 'location':
      return <MarkerIcon width={16} height={16} />;
    default:
      return <MagnifyingGlassIcon width={16} height={16} />;
  }
}

/**
 * Icon for a `PreparedItem.icon` slug. Mirrors `MapSearchDropdown`'s
 * mapping for the scoped-result slugs this palette can produce
 * (`transcript`, `message`, `place`); other slugs would only appear if
 * a future scoped command starts surfacing different result types,
 * in which case we fall through to the generic glyph.
 */
function iconForPreparedItem(icon: PreparedItem['icon']): React.ReactNode {
  switch (icon) {
    case 'transcript':
      return <EPTranscript width={16} height={16} />;
    case 'message':
      return <EPChat width={16} height={16} />;
    case 'call':
      return <CallSharedIcon width={16} height={16} />;
    case 'place':
      return <MarkerIcon width={16} height={16} />;
    case 'gis':
    case 'static':
    case 'dynamic':
      return <MapLayerIcon width={16} height={16} />;
    default:
      return <MagnifyingGlassIcon width={16} height={16} />;
  }
}

/**
 * Map a scoped-command filter slug to a human label for the header /
 * empty state copy. Kept tiny and local — these are all the slugs the
 * shipped scoped commands produce.
 */
function noun(scopedKind: string | undefined): {
  singular: string;
  plural: string;
} {
  switch (scopedKind) {
    case 'transcript':
      return { singular: 'transcript', plural: 'transcripts' };
    case 'message':
      return { singular: 'message', plural: 'messages' };
    case 'parcel':
      return { singular: 'parcel', plural: 'parcels' };
    case 'location':
      return { singular: 'location', plural: 'locations' };
    default:
      return { singular: 'result', plural: 'results' };
  }
}

interface CommandPaletteProps {
  dropdownRef: React.RefObject<HTMLDivElement>;
  /**
   * Pre-filtered, capability-gated command suggestions. Used when the
   * user is still typing the command name (catalog mode).
   */
  suggestions: CommandDef[];
  /**
   * Whether the user has typed only `/` so far (no command name yet).
   * Used to switch the header label between "Available commands" (the
   * empty-prefix catalog state) and "Matching commands" (typing).
   */
  isCatalog: boolean;
  /**
   * True when the palette should render `argSuggestions` instead of
   * `suggestions`. The host derives this from `useCommandMode`.
   */
  isArgMode: boolean;
  /** Live argument autocomplete rows, surfaced while in arg mode. */
  argSuggestions: ArgSuggestion[];
  /**
   * The committed action command driving arg mode — used to pick
   * the leading icon for each row (every arg row inherits the
   * command's icon since it represents a candidate value for that
   * command).
   */
  argCommandId?: string;
  /**
   * True when the palette should render scoped-search results (e.g.
   * `/transcript foo` or `/location main st` hits) in place of the
   * catalog or arg list.
   * Wins over `isArgMode` and the catalog when set, since the user
   * is plainly past the autocomplete stage.
   */
  isScopedResultsMode: boolean;
  /** Slash-command filter slug, e.g. `'transcript'` or `'message'`. */
  scopedKind?: string;
  /** Verbatim query text after the slash command, used in copy. */
  scopedQuery: string;
  /**
   * Result rows for the active scoped-search command. Already
   * prepared / grouped by the host's `useGroupedResults` so this
   * stays render-only.
   */
  scopedResults: PreparedItem[];
  /** True while the scoped executor is still running. */
  scopedIsSearching: boolean;
  /** Optional generic fallback when underlying scoped data isn't available. */
  scopedEmptyMessage?: string;
  cursor: number;
  onCursorChange: (idx: number) => void;
  /** Run a command suggestion (catalog mode). */
  onExecute: (cmdId: string) => void;
  /** Pick an arg suggestion (arg mode) — host decides Tab vs Enter. */
  onPickArg: (suggestion: ArgSuggestion) => void;
  /** Pick a scoped-search result row — host dispatches the action. */
  onPickScopedResult: (prepared: PreparedItem) => void;
}

/**
 * Pre-execute palette surface — owns the entire slash-command UI
 * surface end-to-end. Everything the user sees while their input
 * starts with `/` lives inside this dropdown; the regular search
 * dropdown stays out of the way completely.
 *
 * Three render modes (priority order):
 *   - **scoped-results**: result rows for `/transcript foo` /
 *     `/chat foo` / `/location 123 Main St` (committed scoped-search
 *     command + long-enough query). Rows render with their own
 *     icons / labels and dispatch the same action a regular result
 *     row would.
 *   - **arg-suggestions**: live autocomplete rows for the committed
 *     action's argument (`/call 88` → matching active calls).
 *   - **catalog**: registered commands that match the typed prefix.
 */
const CommandPalette: React.FC<CommandPaletteProps> = ({
  dropdownRef,
  suggestions,
  isCatalog,
  isArgMode,
  argSuggestions,
  argCommandId,
  isScopedResultsMode,
  scopedKind,
  scopedQuery,
  scopedResults,
  scopedIsSearching,
  scopedEmptyMessage,
  cursor,
  onCursorChange,
  onExecute,
  onPickArg,
  onPickScopedResult,
}) => {
  const view: 'scoped' | 'arg' | 'catalog' = isScopedResultsMode
    ? 'scoped'
    : isArgMode
      ? 'arg'
      : 'catalog';

  const scopedNoun = noun(scopedKind);

  let headerLabel: string;
  if (view === 'scoped') {
    headerLabel = scopedIsSearching
      ? `Searching ${scopedNoun.plural}…`
      : `${scopedNoun.plural.replace(/^./, c => c.toUpperCase())} matching “${scopedQuery}”`;
  } else if (view === 'arg') {
    headerLabel = `Suggestions for /${argCommandId ?? ''}`.trim();
  } else if (isCatalog) {
    headerLabel = 'Available commands';
  } else {
    headerLabel = 'Matching commands';
  }

  let headerHint: React.ReactNode;
  if (view === 'scoped') {
    headerHint = (
      <>
        <span className={styles.paletteHeaderHintKey}>Enter</span> to open
      </>
    );
  } else if (view === 'arg') {
    headerHint = (
      <>
        <span className={styles.paletteHeaderHintKey}>Tab</span> to autocomplete
        {' · '}
        <span className={styles.paletteHeaderHintKey}>Enter</span> to run
      </>
    );
  } else {
    headerHint = (
      <>
        <span className={styles.paletteHeaderHintKey}>Tab</span> to autocomplete
      </>
    );
  }

  return (
    <Dropdown ref={dropdownRef} onMouseDown={e => e.preventDefault()}>
      <div className={styles.paletteHeader}>
        <span className={styles.paletteHeaderLabel}>{headerLabel}</span>
        <span className={styles.paletteHeaderHint}>{headerHint}</span>
      </div>

      {view === 'scoped' && (
        <ScopedResultRows
          scopedResults={scopedResults}
          scopedKind={scopedKind}
          scopedQuery={scopedQuery}
          scopedIsSearching={scopedIsSearching}
          scopedEmptyMessage={scopedEmptyMessage}
          cursor={cursor}
          onCursorChange={onCursorChange}
          onPickScopedResult={onPickScopedResult}
        />
      )}
      {view === 'arg' &&
        renderArgRows({
          argSuggestions,
          argCommandId,
          cursor,
          onCursorChange,
          onPickArg,
        })}
      {view === 'catalog' &&
        renderCommandRows({
          suggestions,
          cursor,
          onCursorChange,
          onExecute,
        })}
    </Dropdown>
  );
};

function renderCommandRows({
  suggestions,
  cursor,
  onCursorChange,
  onExecute,
}: Pick<
  CommandPaletteProps,
  'suggestions' | 'cursor' | 'onCursorChange' | 'onExecute'
>) {
  if (suggestions.length === 0) {
    return (
      <EmptyState>
        No matching commands. Type <code>/</code> to see what&rsquo;s available.
      </EmptyState>
    );
  }
  return suggestions.map((def, idx) => {
    const isActive = cursor === idx;
    return (
      <ResultItem
        key={def.id}
        data-result-index={idx}
        isActive={isActive}
        onClick={() => onExecute(def.id)}
        onMouseEnter={() => onCursorChange(idx)}
      >
        <ResultIcon>{iconForCommand(def.id)}</ResultIcon>
        <ResultContent>
          <div className={styles.commandSyntax}>
            <span className={styles.commandName}>/{def.id}</span>
            {def.usage && (
              <span className={styles.commandUsage}>{def.usage}</span>
            )}
            <span>{def.title}</span>
          </div>
          <div className={styles.commandDescription}>{def.description}</div>
        </ResultContent>
      </ResultItem>
    );
  });
}

function renderArgRows({
  argSuggestions,
  argCommandId,
  cursor,
  onCursorChange,
  onPickArg,
}: Pick<
  CommandPaletteProps,
  'argSuggestions' | 'argCommandId' | 'cursor' | 'onCursorChange' | 'onPickArg'
>) {
  if (argSuggestions.length === 0) {
    return (
      <EmptyState>
        Unable to find data. Keep typing or press Esc to cancel.
      </EmptyState>
    );
  }
  return argSuggestions.map((s, idx) => {
    const isActive = cursor === idx;
    return (
      <ResultItem
        key={s.id}
        data-result-index={idx}
        isActive={isActive}
        onClick={() => onPickArg(s)}
        onMouseEnter={() => onCursorChange(idx)}
      >
        <ResultIcon>{iconForCommand(argCommandId ?? '')}</ResultIcon>
        <ResultContent>
          <ResultLabel>{s.label}</ResultLabel>
          {s.sublabel && <ResultSublabel>{s.sublabel}</ResultSublabel>}
        </ResultContent>
      </ResultItem>
    );
  });
}

function ScopedResultRows({
  scopedResults,
  scopedKind,
  scopedQuery,
  scopedIsSearching,
  scopedEmptyMessage,
  cursor,
  onCursorChange,
  onPickScopedResult,
}: Pick<
  CommandPaletteProps,
  | 'scopedResults'
  | 'scopedKind'
  | 'scopedQuery'
  | 'scopedIsSearching'
  | 'scopedEmptyMessage'
  | 'cursor'
  | 'onCursorChange'
  | 'onPickScopedResult'
>) {
  // Highlight the user's query inside each row's label / sublabel so
  // it's immediately obvious what matched and where. The sublabel
  // itself is constructed by `useGroupedResults` to embed the field
  // name (e.g. "Owner: John Smith", "Status: Medical", "Speaker:
  // \"...\""), so the field tells you where and the highlight tells
  // you what.
  const highlightRegex = useMemo(
    () => buildHighlightRegex(scopedQuery),
    [scopedQuery],
  );

  // Streaming sources flush partial results, so we render whatever we
  // have even while `scopedIsSearching` is still true; the header is
  // what tells the user the run isn't finished yet. Only when there's
  // *nothing* on screen does an empty state make sense.
  if (scopedResults.length === 0) {
    if (scopedIsSearching) {
      return <EmptyState>Searching…</EmptyState>;
    }
    if (scopedEmptyMessage) {
      return <EmptyState>{scopedEmptyMessage}</EmptyState>;
    }
    const { plural } = noun(scopedKind);
    return (
      <EmptyState>
        No {plural} match “{scopedQuery}”.
      </EmptyState>
    );
  }
  return (
    <>
      {scopedResults.map((prepared, idx) => {
        const isActive = cursor === idx;
        return (
          <ResultItem
            key={`scoped-${prepared.flatIdx}-${idx}`}
            data-result-index={idx}
            isActive={isActive}
            onClick={() => onPickScopedResult(prepared)}
            onMouseEnter={() => onCursorChange(idx)}
          >
            <ResultIcon>{iconForPreparedItem(prepared.icon)}</ResultIcon>
            <ResultContent>
              <ResultLabel>
                <HighlightedText text={prepared.label} regex={highlightRegex} />
              </ResultLabel>
              {prepared.sublabel && (
                <ResultSublabel>
                  <HighlightedText
                    text={prepared.sublabel}
                    regex={highlightRegex}
                  />
                </ResultSublabel>
              )}
            </ResultContent>
            {prepared.distanceLabel && (
              <span className={styles.distanceBadge}>
                {prepared.distanceLabel}
              </span>
            )}
          </ResultItem>
        );
      })}
    </>
  );
}

export default CommandPalette;
